import React, {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  ControlledDateSelect,
  ControlledSelect,
  ControlledTimeInput,
} from '@uikit/FormItems/ControlledItems';
import { useForm } from '@hooks/useDirtyForm';
import { idType, infoType } from '@src/types';
import { useSelectOptions } from '@hooks/useSelectOptions';
import { validations } from '@utils/validations';
import { Control } from 'react-hook-form';
import { FilterErrorIndicator, OperatorInfo, SelectType } from '../constants';
import InfoTooltip from '@src/uikit/Info/InfoTooltip/InfoTooltip';

interface InputProps {
  name?: string;
  errorIndicator?: FilterErrorIndicator;
  title?: string;
  info?: infoType;
  placeholder?: string;
  type?:
    | 'Select'
    | 'Date'
    | 'Text'
    | 'TimeClock'
    | 'Boolean'
    | 'Number'
    | 'localSelect'
    | string;

  maxWidth?: string;
  dataTestId?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  selectsRange?: boolean;
  groupFilter?: OperatorInfo | null;
  requestByAsync?: (param: any) => Promise<any>;
  inputValue?:
    | string
    | number
    | null
    | Date
    | Date[]
    | number[]
    | { id: idType; value: boolean; label: string };
  setErrorIndicator?: Dispatch<SetStateAction<FilterErrorIndicator>>;

  setInputValue: (
    value: string | number | null | Date | Date[] | number[]
  ) => void;
  handleKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const normalizeComparableValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).toLowerCase().trim();
};

const CustomInput: FC<InputProps> = ({
  groupFilter,
  name,
  selectsRange,
  placeholder,
  maxWidth,
  disabled,
  type = 'Text',
  min,
  max,
  inputValue,
  setInputValue,
  errorIndicator,
  handleKeyDown,
  setErrorIndicator,
  dataTestId,
  requestByAsync,
}) => {
  const selectName = groupFilter?.selectName as SelectType | undefined;

  const { selectOptions, infoLabel } = useSelectOptions(selectName);

  const { control, reset } = useForm<Control>();

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement> | Date | Date[] | string
  ) => {
    const newValue =
      e instanceof Date || Array.isArray(e)
        ? e
        : ((e as ChangeEvent<HTMLInputElement>).target?.value ?? e);

    if (
      type !== 'number' ||
      !isNaN(Number((newValue as string).replaceAll(',', '')))
    ) {
      setInputValue(
        type === 'number'
          ? +newValue
          : type === 'time'
            ? newValue + ':00'
            : newValue
      );
    }
  };

  useEffect(() => {
    if (!inputValue && inputValue !== 0) {
      reset();
    } else {
      setErrorIndicator?.((prev) => ({ ...prev, value: false }));
    }
  }, [inputValue, reset, setErrorIndicator]);

  const prevSelectNameRef = useRef(selectName);

  useEffect(() => {
    if (prevSelectNameRef.current !== selectName) {
      prevSelectNameRef.current = selectName;
      reset();
    }
  }, [selectName, reset]);

  const normalizedType = (type || 'text').toLowerCase();
  const hasValueError = !!errorIndicator?.value;

  const normalizedSelectValue = useMemo(() => {
    if (normalizedType !== 'select') return undefined;

    if (!Array.isArray(inputValue)) return inputValue;

    const getOptionIdentifier = (option: any) =>
      normalizeComparableValue(option?.value ?? option?.id ?? option?.name);

    return inputValue
      .map((selectedItem) => {
        const selectedIdentifier =
          typeof selectedItem === 'object' && selectedItem !== null
            ? getOptionIdentifier(selectedItem)
            : normalizeComparableValue(selectedItem);

        return selectOptions.find(
          (option: any) => getOptionIdentifier(option) === selectedIdentifier
        );
      })
      .filter(Boolean)
      .map((option: any) => ({
        ...option,
        value: option?.value ?? option?.id ?? option?.name,
        label: option?.label ?? option?.value,
      }));
  }, [inputValue, normalizedType, selectOptions]);

  const normalizedLocalSelectValue = useMemo(() => {
    if (normalizedType !== 'boolean' && normalizedType !== 'localselect') {
      return undefined;
    }

    if (inputValue === null || inputValue === undefined) return null;

    if (typeof inputValue === 'object' && inputValue !== null) {
      return inputValue;
    }

    return (
      groupFilter?.booleanOptions?.find(
        (option) =>
          normalizeComparableValue(option.value) ===
            normalizeComparableValue(inputValue) ||
          normalizeComparableValue(option.id) ===
            normalizeComparableValue(inputValue)
      ) || null
    );
  }, [groupFilter?.booleanOptions, inputValue, normalizedType]);

  const commonSelectProps = {
    control,
    changeSelect: setInputValue,
    requestByAsync,
    rules: {
      validate: validations.selectRequired(infoLabel?.label || ''),
    },
  };

  const components = useMemo(() => {
    const textOrNumberInput = (
      <input
        data-test-id={`${dataTestId}-custom-input`}
        key={name}
        className="truncate-input filtration-custom-input"
        id={name}
        type={type.toLowerCase()}
        placeholder={placeholder}
        value={inputValue as string | undefined}
        min={min}
        max={max}
        onKeyDown={handleKeyDown}
        onChange={handleInputChange}
        disabled={disabled}
        style={{
          maxWidth,
          height: '40px',
          border:
            errorIndicator?.value == null || errorIndicator?.value === false
              ? 'none'
              : '1px solid red',
        }}
      />
    );

    const localSelectInput = (
      <div
        key={`local-${selectName}`}
        className={`filter-select-container ${hasValueError ? 'filter-error' : ''}`}
      >
        <ControlledSelect
          {...commonSelectProps}
          name={infoLabel?.name || 'status'}
          selectOptions={groupFilter?.booleanOptions || []}
          value={normalizedLocalSelectValue}
          disabled={disabled}
          dataTestId={`${dataTestId}-custom-select`}
        />
      </div>
    );

    return {
      select: (
        <div
          key={`select-${selectName}`}
          className={`filter-select-container ${hasValueError ? 'filter-error' : ''}`}
        >
          <ControlledSelect
            key={selectName}
            {...commonSelectProps}
            name={infoLabel?.name || 'lob'}
            selectOptions={selectOptions}
            value={normalizedSelectValue}
            isMulti
            disabled={disabled}
            dataTestId={`${dataTestId}-custom-select`}
          />
        </div>
      ),
      date: (
        <ControlledDateSelect
          errorIndicator={errorIndicator}
          control={control}
          name={name}
          disabled={disabled}
          selectsRange={selectsRange}
          placeholder="mm/dd/yyyy"
          rules={{ required: true }}
          onChange={handleInputChange}
          dataTestId={`${dataTestId}-custom-date-select`}
        />
      ),
      text: textOrNumberInput,
      number: textOrNumberInput,
      time: (
        <ControlledTimeInput
          key={name}
          name={groupFilter?.selectName || 'loadDuration'}
          control={control}
          disabled={disabled}
          mask="99:99"
          placeholder="HH:MM"
          onChange={handleInputChange}
          dataTestId={`${dataTestId}-custom-time-input`}
        />
      ),
      boolean: localSelectInput,
      localSelect: localSelectInput,
    };
  }, [
    control,
    selectOptions,
    handleInputChange,
    selectsRange,
    errorIndicator?.value,
    groupFilter?.selectName,
    groupFilter?.booleanOptions,
    normalizedSelectValue,
    normalizedLocalSelectValue,
    commonSelectProps,
    infoLabel?.name,
    selectName,
    name,
    type,
    placeholder,
    inputValue,
    min,
    max,
    handleKeyDown,
    disabled,
    maxWidth,
  ]);

  // todo: add tooltip for array of dates//
  const getTooltipTitle = (): string => {
    if (inputValue == null) return '';
    if (Array.isArray(inputValue)) {
      if (inputValue.length === 0) return '';
      return inputValue
        .map((item) => {
          if (item == null) return '';
          if (typeof item === 'object' && 'label' in item) {
            return String((item as { label: string }).label);
          }
          return String(item);
        })
        .filter(Boolean)
        .join(' - ');
    }
    if (inputValue instanceof Date) {
      return inputValue.toLocaleDateString();
    }
    return String(inputValue);
  };

  return (
    <InfoTooltip title={getTooltipTitle()}>
      <div
        className={`filtration-input input-text ${hasValueError ? 'filter-error' : ''}`}
      >
        {components[normalizedType]}
      </div>
    </InfoTooltip>
  );
};

export default CustomInput;
